// @ts-ignore — JS module without types
import { apiClient, apiEndpoint } from '../../../../services/api.services';
// @ts-ignore — JS module without types
import { API } from '../../../../constants/api.constants';
import type { Asset, AssetFilters, AssetLocationSection, CreateAssetPayload, UpdateAssetPayload } from '../types';
import { logError } from '../../../../utils/logger';

const BASE = API.ASSET_LIBRARY_ASSETS.BASE_PATH as string;
const FILTER_VALUES_BY_KEY = API.ASSET_LIBRARY_FILTER.VALUES_BY_KEY as string;
const ASSETS_TAGS = API.ASSET_LIBRARY_ASSETS.TAGS as string;
const SHARE_CONTACTS = API.ASSET_LIBRARY_ASSETS.SHARE_CONTACTS as string;
const COMPANY_USERS = API.ASSET_LIBRARY_ASSETS.COMPANY_USERS as string;
const CUSTOM_FORMATS = API.ASSET_LIBRARY_ASSETS.CUSTOM_FORMATS as string;

/** Standard Bombiix API response envelope (see docs/api-conventions.md). */
export interface ApiResponse<TData = unknown> {
  code?: number;
  message?: string;
  data?: TData;
}

/** Shape of `data` on an error envelope. */
export interface ApiErrorData {
  errMsg?: string;
}

export interface CustomFormatRecord {
  _id: string;
  name: string;
  category: 'image' | 'video' | 'document';
  formatType: string;
  width: number | null;
  height: number | null;
}

export interface CreateCustomFormatPayload {
  name: string;
  category: string;
  formatType: string;
  width: number | null;
  height: number | null;
}

export interface AssetFilterOptionValue {
  _id: string;
  label: string;
}

export interface AssetFilterType {
  _id: string;
  filterKey: string;
  label: string;
  isActive: boolean;
  values: AssetFilterOptionValue[];
}

export interface AssetCompanyUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface AssetFolderOption {
  _id: string;
  name: string;
}

export interface AssetFolderOptionsData {
  all: AssetFolderOption[];
  [key: string]: unknown;
}

export interface AssetProductOption {
  _id: string;
  number: string;
}

export interface AssetLocationsData {
  sections: AssetLocationSection[];
}

export interface ShareContact {
  _id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/** Fetch all distinct tag strings from the assets collection for autocomplete. */
export const getAssetTagSuggestions = async (): Promise<ApiResponse<string[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${ASSETS_TAGS}`);
  } catch (error) {
    logError('Error: asset-service: getAssetTagSuggestions:', error);
    throw error;
  }
};

/** Fetch the current user's saved custom download formats. */
export const getCustomFormats = async (): Promise<ApiResponse<CustomFormatRecord[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${CUSTOM_FORMATS}`);
  } catch (error) {
    logError('Error: asset-service: getCustomFormats:', error);
    throw error;
  }
};

/** Persist a new custom download format for the current user. */
export const createCustomFormat = async (payload: CreateCustomFormatPayload): Promise<ApiResponse<CustomFormatRecord>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${CUSTOM_FORMATS}`, payload);
  } catch (error) {
    logError('Error: asset-service: createCustomFormat:', error);
    throw error;
  }
};

/** Per-asset instruction for a download request — mirrors the server's DownloadRequestItem shape. */
export interface DownloadRequestItem {
  assetId: string;
  action: 'video' | 'image' | 'pdf' | 'document-pdf' | 'original';
  formatId?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  fileName?: string;
}

export interface DownloadRequestResult {
  /** Set when the request was served immediately — a single asset needing no conversion. */
  downloadUrl?: string;
  /** Set when a job was queued; poll `getDownloadJob` or wait for the socket event. */
  jobId?: string;
  mode: 'single' | 'bulk';
  status: 'Ready' | 'Pending';
}

export interface DownloadJobStatus {
  jobId: string;
  status: 'Pending' | 'InProgress' | 'Completed' | 'Failed';
  mode: 'single' | 'bulk';
  fileName: string | null;
  errorMessage: string | null;
  downloadUrl: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * Requests a download of one or more assets.
 *
 * Answers 200 with `downloadUrl` when the selection is a single unconverted asset, and 202 with
 * `jobId` otherwise — conversions and multi-asset ZIPs are built asynchronously by the download
 * Lambda, so the request returns as soon as the job is registered rather than holding the
 * connection open for the whole conversion.
 */
export const requestAssetDownload = async (
  items: DownloadRequestItem[],
  uploadSocketId?: string,
): Promise<ApiResponse<DownloadRequestResult>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/download-request`, { items, uploadSocketId });
  } catch (error) {
    logError('Error: asset-service: requestAssetDownload:', error);
    throw error;
  }
};

/**
 * Reads a download job's current state. The presigned URL is minted per read, so a link fetched
 * long after completion still works — this is the durable fallback when the socket event is missed.
 */
export const getAssetDownloadJob = async (jobId: string): Promise<ApiResponse<DownloadJobStatus>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/download-jobs/${jobId}`);
  } catch (error) {
    logError('Error: asset-service: getAssetDownloadJob:', error);
    throw error;
  }
};

/**
 * Re-encodes a video asset to a built-in download preset and returns a presigned download URL.
 * @deprecated Use `requestAssetDownload`. Synchronous, so it is capped at ~30 s of conversion work.
 */
export const convertAssetVideo = async (assetId: string, formatId: string): Promise<any> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/${assetId}/convert-video`, { formatId });
  } catch (error) {
    logError('Error: asset-service: convertAssetVideo:', error);
    throw error;
  }
};

/**
 * Re-encodes/resizes an image asset to the given mime type (+ optional custom width/height) and
 * returns a presigned download URL.
 * @deprecated Use `requestAssetDownload`.
 */
export const convertAssetImage = async (assetId: string, mimeType: string, width?: number, height?: number): Promise<any> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/${assetId}/convert-image`, { mimeType, width, height });
  } catch (error) {
    logError('Error: asset-service: convertAssetImage:', error);
    throw error;
  }
};

/**
 * Renders an image asset to a single-page PDF and returns a presigned download URL.
 * @deprecated Use `requestAssetDownload`.
 */
export const convertAssetImageToPdf = async (assetId: string): Promise<any> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/${assetId}/convert-image-to-pdf`);
  } catch (error) {
    logError('Error: asset-service: convertAssetImageToPdf:', error);
    throw error;
  }
};

/**
 * Per-asset instruction for bulkDownloadZip — mirrors the server's DownloadZipItem shape.
 * @deprecated Use `DownloadRequestItem`.
 */
export interface DownloadZipItem {
  assetId: string;
  action: 'video' | 'image' | 'pdf' | 'document-pdf' | 'original';
  formatId?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  fileName?: string;
}

/**
 * Resolves N assets (with optional per-asset conversion) into a single ZIP and returns a presigned
 * download URL.
 * @deprecated Use `requestAssetDownload`. Builds the ZIP inside the browser's own request.
 */
export const bulkDownloadZip = async (items: DownloadZipItem[]): Promise<ApiResponse<{ downloadUrl: string }>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/bulk-download-zip`, { items });
  } catch (error) {
    logError('Error: asset-service: bulkDownloadZip:', error);
    throw error;
  }
};

export const getFilterValuesByKey = async (filterKey: string) => {
  try {
    return await apiClient.post(`${apiEndpoint}${FILTER_VALUES_BY_KEY}`, { filterKey });
  } catch (error) {
    logError('Error: asset-service: getFilterValuesByKey:', error);
    throw error;
  }
};

/** Fetch all filter types (image_type, produced_by, usage_rights) with their isActive flag and values. */
export const getAssetFilters = async (): Promise<ApiResponse<AssetFilterType[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.ASSET_LIBRARY_FILTER.BASE_PATH}`);
  } catch (error) {
    logError('Error: asset-service: getAssetFilters:', error);
    throw error;
  }
};

export const getAssets = async (
  filters: AssetFilters,
  page: number,
  limit: number,
): Promise<ApiResponse<{ assets: Asset[]; total: number; page: number; limit: number }>> => {
  try {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    /** Multi-select filters — append each value separately (?imageTypeIds=a&imageTypeIds=b) */
    if (filters.imageTypeIds?.length) {
      filters.imageTypeIds.forEach((id) => params.append('imageTypeIds', id));
    }
    if (filters.producedByIds?.length) {
      filters.producedByIds.forEach((id) => params.append('producedByIds', id));
    }
    if (filters.usageRightsIds?.length) {
      filters.usageRightsIds.forEach((id) => params.append('usageRightsIds', id));
    }
    if (filters.channelIds?.length) {
      filters.channelIds.forEach((id) => params.append('channelIds', id));
    }
    if (filters.seasonIds?.length) {
      filters.seasonIds.forEach((id) => params.append('seasonIds', id));
    }
    if (filters.brandIds?.length) {
      filters.brandIds.forEach((id) => params.append('brandIds', id));
    }
    if (filters.subBrandId) params.set('subBrandId', filters.subBrandId);
    if (filters.licensorIds?.length) {
      filters.licensorIds.forEach((id) => params.append('licensorIds', id));
    }
    if (filters.licenseIds?.length) {
      filters.licenseIds.forEach((id) => params.append('licenseIds', id));
    }
    if (filters.likenessIds?.length) {
      filters.likenessIds.forEach((id) => params.append('likenessIds', id));
    }
    if (filters.tags?.length) {
      filters.tags.forEach((tag) => params.append('tags', tag));
    }
    if (filters.assetCategories?.length) {
      filters.assetCategories.forEach((cat) => params.append('assetCategories', cat));
    }
    if (filters.orientation) params.set('orientation', filters.orientation);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.search) params.set('search', filters.search);
    if (filters.publicationDate) params.set('publicationDate', filters.publicationDate);
    if (filters.duration) params.set('duration', filters.duration);
    if (filters.resolution) params.set('resolution', filters.resolution);
    if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
    return await apiClient.get(`${apiEndpoint}${BASE}/assets?${params.toString()}`);
  } catch (error) {
    logError('Error: asset-service: getAssets:', error);
    throw error;
  }
};

/**
 * Fetch a presigned URL for a browser-viewable thumbnail of an image asset. Used for formats
 * the browser cannot decode itself (TIFF, HEIC) — the server generates the thumbnail from the
 * original on first request and caches it in S3.
 */
export const getAssetThumbnailUrl = async (assetId: string): Promise<ApiResponse<{ url: string }>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/${assetId}/thumbnail`);
  } catch (error) {
    logError('Error: asset-service: getAssetThumbnailUrl:', error);
    throw error;
  }
};

export const getAsset = async (assetId: string): Promise<ApiResponse<Asset>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/${assetId}`);
  } catch (error) {
    logError('Error: asset-service: getAsset:', error);
    throw error;
  }
};

export const createAsset = async (payload: CreateAssetPayload): Promise<ApiResponse<Asset>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets`, payload);
  } catch (error) {
    logError('Error: asset-service: createAsset:', error);
    throw error;
  }
};

export const updateAsset = async (assetId: string, payload: UpdateAssetPayload): Promise<ApiResponse<Asset>> => {
  try {
    return await apiClient.patch(`${apiEndpoint}${BASE}/assets/${assetId}`, payload);
  } catch (error) {
    logError('Error: asset-service: updateAsset:', error);
    throw error;
  }
};

export const deleteAsset = async (assetId: string): Promise<ApiResponse<ApiErrorData>> => {
  try {
    return await apiClient.delete(`${apiEndpoint}${BASE}/assets/${assetId}`);
  } catch (error) {
    logError('Error: asset-service: deleteAsset:', error);
    throw error;
  }
};

export const bulkDelete = async (assetIds: string[]): Promise<ApiResponse<ApiErrorData>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/bulk-delete`, { assetIds });
  } catch (error) {
    logError('Error: asset-service: bulkDelete:', error);
    throw error;
  }
};

export const duplicateAsset = async (assetId: string): Promise<ApiResponse<Asset>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/${assetId}/duplicate`);
  } catch (error) {
    logError('Error: asset-service: duplicateAsset:', error);
    throw error;
  }
};

export const bulkDownload = async (assetIds: string[]): Promise<ApiResponse<unknown>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/bulk-download`, { assetIds });
  } catch (error) {
    logError('Error: asset-service: bulkDownload:', error);
    throw error;
  }
};

/** Fetch modules/locations where the given asset is referenced across products. */
export const getAssetLocations = async (assetId: string): Promise<ApiResponse<AssetLocationsData>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/${assetId}/locations`);
  } catch (error) {
    logError('Error: asset-service: getAssetLocations:', error);
    throw error;
  }
};

/** Save the comments string on an asset (writes to assets.comments via PATCH). */
export const saveAssetComments = async (assetId: string, comments: string): Promise<ApiResponse<Asset>> => {
  try {
    return await apiClient.patch(`${apiEndpoint}${BASE}/assets/${assetId}`, { comments });
  } catch (error) {
    logError('Error: asset-service: saveAssetComments:', error);
    throw error;
  }
};

export const shareAssets = async (payload: {
  assetIds: string[];
  folderName: string;
  description?: string;
  subject?: string;
  message?: string;
  contacts: Array<{ email: string; firstName?: string; lastName?: string }>;
}): Promise<ApiResponse<unknown>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${BASE}/assets/share`, payload);
  } catch (error) {
    logError('Error: asset-service: shareAssets:', error);
    throw error;
  }
};

/** Fetch merged contact list: company users + external contacts from past share records. */
export const getShareContacts = async (): Promise<ApiResponse<ShareContact[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${SHARE_CONTACTS}`);
  } catch (error) {
    logError('Error: asset-service: getShareContacts:', error);
    throw error;
  }
};

/** Fetch all active company users (id + name) for the asset permissions dropdown. */
export const getAssetCompanyUsers = async (): Promise<ApiResponse<AssetCompanyUser[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${COMPANY_USERS}`);
  } catch (error) {
    logError('Error: asset-service: getAssetCompanyUsers:', error);
    throw error;
  }
};

/** Fetch asset library home data; `.data.all` contains the flat assetLibraryQuickLinks list used for the folder dropdown. */
export const getAssetFolderOptions = async (): Promise<ApiResponse<AssetFolderOptionsData>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.ASSET_LIBRARY_HOME.BASE_PATH}/home`);
  } catch (error) {
    logError('Error: asset-service: getAssetFolderOptions:', error);
    throw error;
  }
};

/** Page size used by the image-picker modal when paging through the asset library. */
export const IMAGE_PICKER_PAGE_SIZE = 15;

/** Asset-library "Locations used" value for the Product Information module. */
export const PRODUCT_INFORMATION_LOCATION = 'Product Information';

/**
 * Fetch image assets filtered by a dynamic location, optional productId, and optional search term.
 * assetCategories is always "image"; includeUnassigned is always true so an asset that is unassigned
 * on either dimension still surfaces — one with no productIds/locationsUsed at all, and equally one
 * filed under this location but not yet linked to any product. Results are paged
 * so the picker can load the next page on scroll; the response carries the matching `total`.
 * allowedImageExtensions is a comma-separated accept list (e.g. '.jpg, .png') whose extensions go
 * as fileExtensions so the server returns only matching files — without it a page can come back
 * almost empty once the client filter runs, and `total` counts rows the picker never shows.
 * Callers still narrow each page with matchesAcceptedExtensions: the server keeps legacy rows whose
 * S3 key has no extension so the client's MIME-type fallback can judge them.
 */
export const getImagesByLocation = async (
  locationsUsed: string,
  productId?: string,
  search?: string,
  allowedImageExtensions?: string,
  page = 1,
  limit: number = IMAGE_PICKER_PAGE_SIZE,
): Promise<ApiResponse<{ assets: Asset[]; total?: number }>> => {
  try {
    const params = new URLSearchParams();
    if (productId) params.append('productIds', productId);
    params.append('locationsUsed', locationsUsed);
    params.append('assetCategories', 'image');
    params.append('isActive', 'true');
    params.append('includeUnassigned', 'true');
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search?.trim()) params.set('search', search.trim());
    parseAcceptedExtensions(allowedImageExtensions).forEach((extension) => params.append('fileExtensions', extension));
    return await apiClient.get(`${apiEndpoint}${BASE}/assets?${params.toString()}`);
  } catch (error) {
    logError('Error: asset-service: getImagesByLocation:', error);
    throw error;
  }
};

/**
 * Fetch image assets for the product context modal, filtering by the product's
 * selected brand, sub-brand, licensor, license, and/or likeness IDs. Results are paged so the
 * picker can load the next page on scroll; the response carries the matching `total`.
 * allowedImageExtensions is a comma-separated accept list sent as fileExtensions, mirroring
 * getImagesByLocation, so the server returns only matching files; callers still narrow each page
 * with matchesAcceptedExtensions to judge legacy rows whose S3 key carries no extension.
 */
export const getImagesByProductContext = async (params: {
  search?: string;
  brandIds?: string[];
  subBrandId?: string | null;
  licensorIds?: string[];
  licenseIds?: string[];
  likenessIds?: string[];
  allowedImageExtensions?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<{ assets: Asset[]; total?: number }>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('locationsUsed', PRODUCT_INFORMATION_LOCATION);
    queryParams.append('assetCategories', 'image');
    queryParams.append('isActive', 'true');
    queryParams.set('page', String(params.page ?? 1));
    queryParams.set('limit', String(params.limit ?? IMAGE_PICKER_PAGE_SIZE));
    if (params.search?.trim()) queryParams.set('search', params.search.trim());
    params.brandIds?.forEach((id) => queryParams.append('brandIds', id));
    if (params.subBrandId) queryParams.set('subBrandId', params.subBrandId);
    params.licensorIds?.forEach((id) => queryParams.append('licensorIds', id));
    params.licenseIds?.forEach((id) => queryParams.append('licenseIds', id));
    params.likenessIds?.forEach((id) => queryParams.append('likenessIds', id));
    parseAcceptedExtensions(params.allowedImageExtensions).forEach((extension) => queryParams.append('fileExtensions', extension));
    return await apiClient.get(`${apiEndpoint}${BASE}/assets?${queryParams.toString()}`);
  } catch (error) {
    logError('Error: asset-service: getImagesByProductContext:', error);
    throw error;
  }
};

/** Fetch all open/active development-order products for the product dropdown on assets. */
export const getAssetProductOptions = async (): Promise<ApiResponse<AssetProductOption[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}/all-open-products`);
  } catch (error) {
    logError('Error: asset-service: getAssetProductOptions:', error);
    throw error;
  }
};

const EXTENSION_MIME_MAP: Record<string, string[]> = {
  pdf: ['application/pdf'],
  ai: ['application/postscript', 'application/illustrator'],
  eps: ['application/postscript'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  xls: ['application/vnd.ms-excel'],
  doc: ['application/msword'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  csv: ['text/csv'],
  txt: ['text/plain'],
  dxf: ['image/vnd.dxf'],
  rul: ['application/octet-stream'],
  wds: ['application/octet-stream'],
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  gif: ['image/gif'],
  webp: ['image/webp'],
  bmp: ['image/bmp'],
  svg: ['image/svg+xml'],
  tif: ['image/tiff'],
  tiff: ['image/tiff'],
  heic: ['image/heic'],
  psd: ['image/vnd.adobe.photoshop'],
};

export const DOCUMENT_PICKER_PAGE_SIZE = 15;

/**
 * Normalises an accept string such as ".pdf, .PNG" to lowercase, dot-less extensions.
 *
 * Untyped `.js` hosts of the picker reach this through an `allowedImageExtensions` prop no compiler
 * checks, and the accept list used to be a RegExp — so a caller left on the old shape is logged and
 * treated as "no accept list" rather than being allowed to throw out of a render and take the whole
 * page down with it.
 */
export const parseAcceptedExtensions = (extensions?: string): string[] => {
  if (!extensions) return [];
  if (typeof extensions !== 'string') {
    logError('Error: asset-service: parseAcceptedExtensions: expected a comma-separated string, received:', extensions);
    return [];
  }
  return extensions
    .split(',')
    .map((extension) => extension.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);
};

/**
 * Asset categories spanned by a set of accepted extensions, derived from the MIME group the
 * uploader classifies each extension under — so an accept list mixing documents and images
 * (".pdf, .png") returns both and neither side is silently dropped from the picker.
 * An empty list keeps the historical document-only default; an unrecognised extension returns
 * no categories at all, leaving the extension filter to narrow the response instead of a guess
 * that could exclude the very asset the caller wants.
 */
const getAssetCategoriesForExtensions = (acceptedExtensions: string[]): string[] => {
  if (acceptedExtensions.length === 0) return ['document'];
  if (acceptedExtensions.some((extension) => !EXTENSION_MIME_MAP[extension])) return [];
  const categories = acceptedExtensions.flatMap((extension) =>
    (EXTENSION_MIME_MAP[extension] ?? []).map((mimeType) => {
      const group = mimeType.split('/')[0];
      return group === 'image' || group === 'video' || group === 'audio' ? group : 'document';
    }),
  );
  return [...new Set(categories)];
};

/**
 * True when an asset's stored file matches one of the accepted extensions. The S3 key always ends
 * in the original extension, so it is authoritative; the stored MIME type is consulted only for
 * legacy rows whose key carries no extension. An empty accept list matches everything.
 */
export const matchesAcceptedExtensions = (asset: { fileUrl?: string | null; fileType?: string | null }, acceptedExtensions: string[]): boolean => {
  if (acceptedExtensions.length === 0) return true;
  const fileName = (asset?.fileUrl ?? '').split('/').pop() ?? '';
  const keyExtension = fileName.includes('.') ? (fileName.split('.').pop() ?? '').toLowerCase() : '';
  if (keyExtension) return acceptedExtensions.includes(keyExtension);
  const allowedMimes = new Set(acceptedExtensions.flatMap((extension) => EXTENSION_MIME_MAP[extension] ?? []));
  return allowedMimes.has(asset?.fileType ?? '');
};

/**
 * Fetch asset-library files linked to a product under the given `locationsUsed` location.
 * `extensions` is a comma-separated accept list (e.g. ".pdf, .png"); every asset category it spans
 * is sent as assetCategories so documents and images both surface, and the extensions themselves
 * go as fileExtensions so the server returns only matching files — without it a page can come back
 * almost empty once the client filter runs, and `total` counts rows the picker never shows.
 * Results are paged so the picker can load the next page on scroll, and the response carries the
 * matching `total`. Callers still narrow each page with matchesAcceptedExtensions: the server keeps
 * legacy rows whose S3 key has no extension so the client's MIME-type fallback can judge them.
 * includeUnassigned is always true so an asset that is unassigned on either dimension still surfaces
 * — one with no productIds/locationsUsed at all, and equally one filed under this location but not
 * yet linked to any product — mirroring getImagesByLocation.
 */
export const getDocumentsByLocation = async (
  productId: string,
  search: string,
  extensions?: string,
  locationsUsed?: string,
  page = 1,
  limit: number = DOCUMENT_PICKER_PAGE_SIZE,
): Promise<ApiResponse<{ assets: Asset[]; total?: number }>> => {
  try {
    const params = new URLSearchParams();
    const acceptedExtensions = parseAcceptedExtensions(extensions);
    getAssetCategoriesForExtensions(acceptedExtensions).forEach((category) => params.append('assetCategories', category));
    acceptedExtensions.forEach((extension) => params.append('fileExtensions', extension));
    params.append('isActive', 'true');
    params.append('includeUnassigned', 'true');
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (productId) params.append('productIds', productId);
    if (locationsUsed) params.append('locationsUsed', locationsUsed);
    if (search?.trim()) params.set('search', search.trim());
    return await apiClient.get(`${apiEndpoint}${BASE}/assets?${params.toString()}`);
  } catch (error) {
    logError('Error: asset-service: getDocumentsByLocation:', error);
    throw error;
  }
};

/** Persist a single external contact immediately when the user clicks "Add". */
export const addExternalContact = async (contact: { email: string; firstName?: string; lastName?: string }): Promise<ApiResponse<ShareContact>> => {
  try {
    return await apiClient.post(`${apiEndpoint}${SHARE_CONTACTS}`, contact);
  } catch (error) {
    logError('Error: asset-service: addExternalContact:', error);
    throw error;
  }
};

/** An unfinished upload the logged-in user can resume, as returned by the pending-uploads API. */
export interface ResumableUploadRecord {
  taskId: string;
  uploadId: string;
  objectKey: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  assetCategory: string | null;
  section: string | null;
  module: string;
  createdAt: string;
}

/** Everything the client needs to continue an unfinished upload where it left off. */
export interface ResumeStateRecord {
  uploadId: string;
  objectKey: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  assetCategory: string | null;
  section: string | null;
  partSize: number;
  partCount: number;
  uploadedPartNumbers: number[];
}

/**
 * Lists every unfinished upload owned by the logged-in user, across pages and sessions. Scoped to
 * the caller server-side, so it never returns another user's uploads.
 */
export const getResumableUploads = async (): Promise<ApiResponse<ResumableUploadRecord[]>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/pending-uploads`);
  } catch (error) {
    logError('Error: asset-service: getResumableUploads:', error);
    throw error;
  }
};

/**
 * Resolves the resume state for one unfinished upload: the S3 uploadId, the original part plan and
 * the parts S3 already stored, so only the missing parts are re-sent.
 */
export const getUploadResumeState = async (taskId: string): Promise<ApiResponse<ResumeStateRecord>> => {
  try {
    return await apiClient.get(`${apiEndpoint}${BASE}/assets/pending-uploads/${taskId}/resume-state`);
  } catch (error) {
    logError('Error: asset-service: getUploadResumeState:', error);
    throw error;
  }
};

/** Discards an unfinished upload: aborts it in S3 and removes its resume record. */
export const discardResumableUpload = async (taskId: string): Promise<ApiResponse<ApiErrorData>> => {
  try {
    return await apiClient.delete(`${apiEndpoint}${BASE}/assets/pending-uploads/${taskId}`);
  } catch (error) {
    logError('Error: asset-service: discardResumableUpload:', error);
    throw error;
  }
};

/**
 * Accept list for the image picker — the only raster photo formats a picture may take, both
 * for a direct upload and for the asset-library search behind the picker. Shared by every host of
 * the picker so the server-side filter and the client-side narrowing can never drift apart.
 */
export const IMAGE_PICKER_EXTENSIONS = '.jpg, .jpeg, .png';
